import { useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Form, Input, Modal, Select, Space, Spin, message } from "antd";
import { deliverShopOrder, getShopOrder, processShopOrderRefund, selfPickupShopOrder } from "@/api/shopOrder";
import type { ShopOrderDelivery, ShopOrderDetail, ShopOrderRow } from "@/api/shopOrder";
import { formatReturnAddress, listShopReturnAddressOptions } from "@/api/shopReturnAddress";
import type { ShopReturnAddress } from "@/api/shopReturnAddress";
import { availableOrderOperations, orderOperationLabel, requiresReturnShipping } from "./orderOperations";
import type { OrderOperation } from "./orderOperations";

interface OrderOperationModalProps {
  row: ShopOrderRow;
  action: OrderOperation;
  onCancel: () => void;
  onSuccess: () => void;
}

interface OperationFields extends Omit<ShopOrderDelivery, "orderId"> {
  auditRemark?: string;
  returnAddressId?: number;
}

export function OrderOperationModal({ row, action, onCancel, onSuccess }: OrderOperationModalProps): React.ReactElement {
  const [form] = Form.useForm<OperationFields>();
  const [detail, setDetail] = useState<ShopOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selfPickup, setSelfPickup] = useState(false);
  const [revision, setRevision] = useState(0);
  const submittingRef = useRef(false);
  const [addresses, setAddresses] = useState<ShopReturnAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesFailed, setAddressesFailed] = useState(false);
  const needsReturnShipping = detail !== null && requiresReturnShipping(detail);
  const canSelectAddress = row.goodsType === 1 && action === "confirmRefund" && needsReturnShipping;
  useEffect(() => {
    if (!canSelectAddress) return;
    const controller = new AbortController();
    setAddresses([]); setAddressesLoading(true); setAddressesFailed(false);
    // 退货退款需运营明确选择本次收件地址；仅退款不加载地址。
    void listShopReturnAddressOptions(controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setAddresses((data ?? []).filter((address) => address.merchantId === 0 && address.status === 1));
    }).catch(() => {
      if (!controller.signal.aborted) setAddressesFailed(true);
    }).finally(() => {
      if (!controller.signal.aborted) setAddressesLoading(false);
    });
    return (): void => controller.abort();
  }, [canSelectAddress, revision]);
  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setLoading(true);
    void getShopOrder(row.id, controller.signal).then((response) => {
      if (controller.signal.aborted) return;
      if (!response.data?.order) {
        void message.error("订单信息加载失败");
        return;
      }
      setDetail(response.data);
    }).catch(() => {
      // 请求层统一提示错误。
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return (): void => controller.abort();
  }, [row.id, revision]);

  const allowed = detail !== null && availableOrderOperations(row.goodsType, detail).includes(action);
  const label = orderOperationLabel(action, detail);
  const requiresReason = action === "rejectRefund" || action === "refuseRefund";
  const handleCancel = (): void => { if (!submittingRef.current) onCancel(); };
  const handleToggleSelfPickup = (): void => {
    setSelfPickup((value) => !value);
    form.setFields([{ name: "logisticsCompany", errors: [] }, { name: "logisticsCode", errors: [] }]);
  };
  const handleSubmit = async (values: OperationFields): Promise<void> => {
    if (!allowed || !detail || submittingRef.current) return;
    const auditRemark = values.auditRemark?.trim();
    if (requiresReason && (!auditRemark || auditRemark.length > 500)) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (action === "deliver") {
        if (selfPickup) await selfPickupShopOrder(detail.order.id);
        else await deliverShopOrder({ orderId: detail.order.id,
          logisticsCompany: values.logisticsCompany.trim(), logisticsCode: values.logisticsCode.trim() });
      } else if (requiresReason) {
        await processShopOrderRefund(action, detail.order.id, auditRemark);
      } else if (canSelectAddress) {
        if (addressesLoading || addressesFailed || !addresses.some((address) => address.id === values.returnAddressId)) {
          void message.error("请选择可用的退货地址");
          return;
        }
        await processShopOrderRefund(action, detail.order.id, undefined, values.returnAddressId);
      } else {
        await processShopOrderRefund(action, detail.order.id);
      }
      void message.success(`${action === "deliver" && selfPickup ? "自提" : label}成功`);
      onSuccess();
    } catch {
      // 请求层展示业务错误；重新核对状态并保留物流输入，便于重试。
      setRevision((value) => value + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  const handleConfirm = (): void => form.submit();
  const order = detail?.order;
  const aftersale = detail?.aftersale;
  const description = action === "deliver" ? (selfPickup
    ? "确认自提后，整单商品变更为待自提，无需填写物流信息。"
    : "确认后将整单商品标记为已发货，请核对收货信息及物流单号。")
    : action === "confirmRefund" ? (needsReturnShipping
      ? "同意后等待用户退货，收到退货后再确认退款。" : "同意后将按整单实付金额直接退款，无需退货。")
    : action === "confirmReceive" ? (needsReturnShipping
      ? "请确认已收到退货。确认后将按整单实付金额退款。" : "确认后将按整单实付金额退款。")
    : "确认后将拒绝本次售后申请，订单恢复到原履约状态。";

  return <Modal open title={label} onCancel={handleCancel} onOk={handleConfirm} okText={action === "deliver" && selfPickup ? "确认自提" : "确认"} cancelText="取消"
    confirmLoading={submitting} okButtonProps={{ disabled: loading || !allowed || (canSelectAddress && (addressesLoading || addressesFailed)), danger: action !== "deliver" }}
    cancelButtonProps={{ disabled: submitting }} closable={!submitting} keyboard={!submitting} maskClosable={false}>
    <Spin spinning={loading}>
      {!loading && detail && !allowed && <Alert type="warning" message="当前订单状态不支持此操作，请关闭后刷新订单" />}
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={submitting || loading || !allowed}>
        {order && <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="订单号">{order.orderNo}</Descriptions.Item>
            <Descriptions.Item label="整单实付金额">{order.payAmount == null ? "-" : `¥${Number(order.payAmount).toFixed(2)}`}</Descriptions.Item>
            {action === "deliver" && <Descriptions.Item label="收货人">{order.receiverName || "-"} / {order.receiverPhone || "-"}</Descriptions.Item>}
            {action === "deliver" && <Descriptions.Item label="收货地址">{[order.receiverProvince, order.receiverCity, order.receiverDistrict, order.receiverAddress].filter(Boolean).join("") || row.receiveAddress || "-"}</Descriptions.Item>}
            {action === "deliver" && <Descriptions.Item label="发货商品"><Space direction="vertical">{detail?.orderItems.map((item) =>
              <span key={item.id}>{item.goodsName || "-"} {item.skuName} × {item.goodsNum ?? "-"}</span>)}</Space></Descriptions.Item>}
            {action !== "deliver" && <Descriptions.Item label="售后类型">{aftersale ? (needsReturnShipping ? "退货退款" : "仅退款") : "-"}</Descriptions.Item>}
            {action !== "deliver" && <Descriptions.Item label="申请原因">{aftersale?.applyReason || "-"}</Descriptions.Item>}
            {action !== "deliver" && needsReturnShipping && aftersale && <Descriptions.Item label="退货物流">{aftersale.returnLogisticsCompany || "-"} / {aftersale.returnLogisticsCode || "-"}</Descriptions.Item>}
          </Descriptions>
          {allowed && <Alert type="warning" showIcon message={description} />}
        </Space>}
        {action === "deliver" && <>
          <Form.Item name="logisticsCompany" label={<Space>物流公司<Button size="small" type={selfPickup ? "primary" : "default"} aria-pressed={selfPickup} onClick={handleToggleSelfPickup}>自提</Button></Space>}
            rules={selfPickup ? [] : [{ required: true, whitespace: true, message: "请输入物流公司" }, { max: 100, message: "物流公司不能超过100个字符" }]}>
            <Input placeholder="请输入物流公司" maxLength={100} disabled={selfPickup} />
          </Form.Item>
          <Form.Item name="logisticsCode" label="物流单号" rules={selfPickup ? [] : [{ required: true, whitespace: true, message: "请输入物流单号" }, { max: 64, message: "物流单号不能超过64个字符" }]}>
            <Input placeholder="请输入物流单号" maxLength={64} disabled={selfPickup} />
          </Form.Item>
        </>}
        {canSelectAddress && <>
          {addressesFailed && <Alert type="error" message="退货地址加载失败，请重新打开弹窗重试" />}
          <Form.Item name="returnAddressId" label="退货地址" rules={[{ required: true, message: "请选择退货地址" }]}>
            <Select showSearch optionFilterProp="label" placeholder="请选择退货地址" loading={addressesLoading}
              options={addresses.map((address) => ({ value: address.id, label: `${address.receiverName} / ${address.receiverPhone} / ${formatReturnAddress(address)}` }))} />
          </Form.Item>
        </>}
        {requiresReason && <Form.Item name="auditRemark" label="驳回原因" rules={[
          { required: true, whitespace: true, message: "请输入驳回原因" },
          { max: 500, message: "驳回原因不能超过500字" },
        ]}>
          <Input.TextArea rows={4} maxLength={500} showCount placeholder="请输入驳回退款的原因" />
        </Form.Item>}
      </Form>
    </Spin>
  </Modal>;
}
